import { LitElement, html } from 'lit';

import { api } from '../api/index.js';
import { loadState, staleWarning } from '../components/load-state.js';
import { when } from '../components/time.js';
import { poll } from '../poll.js';
import { formStyles } from '../../styles/shared/forms.js';
import { pageStyles } from '../../styles/shared/page.js';
import { TableLabels } from '../table-labels.js';
import { tableStyles } from '../../styles/shared/tables.js';
import { templatesPageStyles } from '../../styles/pages/templates-page.js';

/**
 * Operator view: the agent templates. A new instance starts from one (the server page), and gets
 * it as its revision 1; its customer makes it their own from there. Changing a template changes
 * none of the instances made from it.
 */
class TemplatesPage extends LitElement {
  static properties = {
    archived: { state: true },
  };

  static styles = [
    pageStyles,
    tableStyles,
    formStyles,
    templatesPageStyles,
  ];

  constructor() {
    super();
    new TableLabels(this);
    this.archived = false;
    this.poll = poll(this, () => api.templates({ archived: this.archived }));
  }

  render() {
    const head = html`
      <header class="page-head">
        <div>
          <div class="page-title">
            <h1>Templates</h1>
            <plane-help topic="templates">
              <p>A template is the configuration a workspace starts from: the parameters you choose, model included, and a persona. A workspace made from it runs it at once; its customer then changes the name, the picture, the persona and who can write to it, while the model stays yours.</p>
              <p>Built-in templates come with plane, which updates them: duplicate one to change it. Saving a template changes no workspace until you update them from its page.</p>
            </plane-help>
          </div>
          <p class="lead">What a new workspace starts from.</p>
        </div>
        <div class="actions">
          <a class="button primary" href="/templates/new">New template</a>
        </div>
      </header>
    `;
    const pending = loadState(this.poll, 'the templates');
    if (pending) {
      return html`${head}${pending}`;
    }
    const templates = this.poll.value;
    const toolbar = html`
      <div class="toolbar">
        <label class="checkbox">
          <input
            type="checkbox"
            .checked=${this.archived}
            @change=${(event) => {
              this.archived = event.target.checked;
              this.poll.refresh();
            }}
          />
          Show archived templates
        </label>
      </div>
    `;
    if (templates.length === 0) {
      return html`${head}${toolbar}<p class="empty">No template yet. Write one, or make one from a workspace that works well (its page, <strong>Make a template</strong>).</p>`;
    }
    return html`
      ${head} ${staleWarning(this.poll)} ${toolbar}
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Template</th>
              <th>Image</th>
              <th class="num">Version</th>
              <th class="num">Assistants</th>
              <th>Changed</th>
            </tr>
          </thead>
          <tbody>
            ${templates.map(
              (template) => html`
                <tr>
                  <td>
                    <a href="/templates/${template.id}">${template.name}</a>
                    ${template.builtIn ? html`<span class="muted" title="Comes with plane, which updates it: duplicate it to change it">(built in)</span>` : null}
                    ${template.archived ? html`<span class="muted">(archived)</span>` : null}
                    ${template.description ? html`<div class="muted">${template.description}</div>` : null}
                  </td>
                  <td><code>${template.imageVersion}</code></td>
                  <td class="num">${template.version}</td>
                  <td class="num">${template.assistants}${template.behind > 0 ? html` <span class="muted" title="Made from an older version">(${template.behind} behind)</span>` : null}</td>
                  <td>${when(template.updatedAt)}${template.updatedBy ? html` <span class="muted">by ${template.updatedBy}</span>` : null}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    `;
  }
}

customElements.define('plane-templates-page', TemplatesPage);
